import { FrontendTaskStatus, TaskStatus } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';

import {
    useCancelTaskExecution,
    useCompleteTask,
    useCreateTask,
    useDeleteTask,
    useUpdateTask,
} from '@common/Task/mutations/taskMutations';
import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';
import { getTaskCreationParams, getTaskUpdateParams } from '@common/Task/TaskRightMenu/utils/taskParams';
import { assertIsDefined } from '@utils/assertIsDefined';

const EXISTING_TASK_MESSAGE = 'Task actions are shown only for a saved task';

interface Data {
    isEditMode: boolean;
    status: TaskStatus | FrontendTaskStatus;
    canEdit: boolean;
    canChangeStatus: boolean;
    onClose(): void;
    onCreate(): Promise<void>;
    onUpdate(): Promise<void>;
    onDelete(): Promise<void>;
    onComplete(): Promise<void>;
    onCancelExecution(): Promise<void>;
    onToggleEditMode(): Promise<void>;
}

export function useTaskActions(): Data {
    const { state, permissions, actions, task } = useTaskForm();
    const createTask = useCreateTask();
    const updateTask = useUpdateTask();
    const deleteTask = useDeleteTask();
    const completeTask = useCompleteTask();
    const cancelExecution = useCancelTaskExecution();

    const onClose = (): void => {
        actions.reset();
        store.app.resetRightMenu();
    };

    const onCreate = async (): Promise<void> => {
        await createTask.mutateAsync(getTaskCreationParams(state));
        onClose();
    };

    const onUpdate = async (): Promise<void> => {
        if (task) {
            const { id, updateParams } = getTaskUpdateParams(state, task);
            await updateTask.mutateAsync({ id, params: updateParams });
        }
        onClose();
    };

    const onDelete = async (): Promise<void> => {
        assertIsDefined(state.taskId, EXISTING_TASK_MESSAGE);
        await deleteTask.mutateAsync({ id: state.taskId });
        onClose();
    };

    const onComplete = async (): Promise<void> => {
        assertIsDefined(state.taskId, EXISTING_TASK_MESSAGE);
        await completeTask.mutateAsync({ id: state.taskId });
        actions.setStatus(TaskStatus.Completed, store.authUser.id);
    };

    const onCancelExecution = async (): Promise<void> => {
        assertIsDefined(state.taskId, EXISTING_TASK_MESSAGE);
        await cancelExecution.mutateAsync({ id: state.taskId });
        actions.setStatus(TaskStatus.Requested);
    };

    const onToggleEditMode = async (): Promise<void> => {
        actions.toggleEditMode();
    };

    return {
        isEditMode: state.isEditMode,
        status: state.status,
        canEdit: permissions.canEdit || permissions.canEditDate,
        canChangeStatus: permissions.userCanChangeStatus,
        onClose,
        onCreate,
        onUpdate,
        onDelete,
        onComplete,
        onCancelExecution,
        onToggleEditMode,
    };
}
